import { Theme } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { useContext } from 'react';
import { type To } from 'react-router-dom';

import StaticPageLayout from '@/Layout/StaticPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import EmptyStateDark from '@/assets/icons/empty-state-dark.svg?react';
import EmptyState from '@/assets/icons/empty-state.svg?react';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import Button from '@/shared/components/Button';
import DynamicT from '@/shared/components/DynamicT';
import NavBar from '@/shared/components/NavBar';
import PageMeta from '@/shared/components/PageMeta';

import SupportInfo from './SupportInfo';
import styles from './index.module.scss';

type Props = {
  readonly title?: TFuncKey;
  readonly rawTitle?: string;
  readonly message?: TFuncKey;
  readonly rawMessage?: string;
  readonly isNavbarHidden?: boolean;
  readonly primaryAction?: {
    readonly title?: TFuncKey;
    readonly label?: string;
    readonly isLoading?: boolean;
    readonly to?: To;
    readonly replace?: boolean;
    readonly onClick?: () => void;
  };
};

const ErrorPage = ({
  title = 'description.not_found',
  rawTitle,
  message,
  rawMessage,
  isNavbarHidden,
  primaryAction,
}: Props) => {
  const { theme } = useContext(PageContext);
  const navigate = useNavigateWithPreservedSearchParams();
  const errorMessage = Boolean(rawMessage ?? message);

  return (
    <StaticPageLayout>
      <PageMeta titleKey={title} />
      {history.length > 1 && (
        <NavBar
          isHidden={isNavbarHidden}
          onBack={() => {
            navigate(-1);
          }}
        />
      )}
      <div className={styles.container}>
        {theme === Theme.Light ? <EmptyState /> : <EmptyStateDark />}
        <div className={styles.title}>{rawTitle ?? <DynamicT forKey={title} />}</div>
        {errorMessage && (
          <div className={styles.message}>{rawMessage ?? <DynamicT forKey={message} />}</div>
        )}
        <SupportInfo />
        {primaryAction && (
          <Button
            className={styles.actionButton}
            isLoading={primaryAction.isLoading}
            label={primaryAction.label}
            title={primaryAction.title}
            onClick={() => {
              if (primaryAction.onClick) {
                primaryAction.onClick();
                return;
              }

              if (primaryAction.to) {
                navigate(primaryAction.to, { replace: primaryAction.replace });
              }
            }}
          />
        )}
      </div>
    </StaticPageLayout>
  );
};

export default ErrorPage;
